import { Formik, Form } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import RowLoader from "../UI/RowLoader";
import {
  ARABIC_REGEX,
  ENGLISH_REGEX,
  IS_ACTIVE,
} from "../../constants/globals";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useState } from "react";
import DropdownForm from "../Form/DropdownForm";
import FormField from "../Form/FormField";
import TextareaForm from "../Form/TextareaForm";
import FileInput from "../Form/FileInput";
import ConfirmCheckBox from "../Form/ConfirmCheckBox";
import { addNewInstructorApi, updateInstructorDataApi } from "../../apis";

const AddOrEditInstructor = ({
  mode = "add",
  initialValuesToUpdate = {},
  imageToUpdate = "",
}) => {
  const { instructorId } = useParams();
  const [image, setImage] = useState(imageToUpdate || "");

  const navigate = useNavigate();

  // Initial form values
  const initialValues =
    mode === "add"
      ? {
          nameEn: "",
          nameAr: "",
          titleEn: "",
          titleAr: "",
          bioEn: "",
          bioAr: "",
          email: "",
          phone: "",
          mailPartner: "",
          phonePartner: "",
          image: "",
          isActive: "1",
          confirm: false,
        }
      : initialValuesToUpdate;

  // Validation schema using Yup
  const StepOneSchema = Yup.object().shape({
    nameEn: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .required("Name in English is required"),
    confirm: Yup.boolean().oneOf([true], "You must confirm your choices"),
    nameAr: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .required("Name in Arabic is required"),
    titleEn: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .required("Title in English is required"),
    titleAr: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .required("Title in Arabic is required"),
    bioEn: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .required("Bio in English is required"),
    bioAr: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .required("Bio in Arabic is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    phone: Yup.string()
      .required("Phone number is required")
      .matches(/^(010|011|012|015)\d{8}$/, {
        message:
          "Phone number must be 11 digits and start with 010, 011, 012, or 015",
        excludeEmptyString: true,
      }),
    mailPartner: Yup.string().email("Please enter a valid email address"),
    phonePartner: Yup.string()
      .matches(/^(010|011|012|015)\d{8}$/, {
        message:
          "Phone number must be 11 digits and start with 010, 011, 012, or 015",
        excludeEmptyString: true,
      })
      .max(11, "Phone number must be 11 digits"),
    isActive: Yup.string().required("required"),
    image: Yup.mixed()
      .test("isRequired", "Image is required", function (value) {
        if (mode === "edit") return true;
        return !!value;
      })
      .test("fileType", "Invalid file format", function (value) {
        if (!value) return true; // Handle optional file input
        const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];
        const fileExtension = value?.name?.split(".").pop().toLowerCase();
        return allowedExtensions.includes(fileExtension);
      })
      .test("fileSize", "File size must be less than 5MB", function (value) {
        if (!value) return true; // Handle optional file input
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        return value?.size <= maxSize;
      }),
  });

  // Form submit handler
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const handleIntialValues = { ...initialValues };
    delete handleIntialValues.confirm;
    const handleValues = { ...values };
    delete handleValues.confirm;
    if (mode === "edit") {
      if (JSON.stringify(handleIntialValues) === JSON.stringify(handleValues)) {
        Swal.fire({
          title: "No changes detected",
          text: "No changes were made to the course, no update was made.",
          icon: "info",
        });
        return;
      }
    }
    try {
      // Create FormData object
      const formData = new FormData();

      // Map Formik values to the API expected fields
      if (mode === "edit") {
        formData.append("_method", "patch");
      }

      formData.append("name_english", values.nameEn);
      formData.append("name_arabic", values.nameAr);
      formData.append("title_english", values.titleEn);
      formData.append("title_arabic", values.titleAr);
      formData.append("bio_english", values.bioEn);
      formData.append("bio_arabic", values.bioAr);
      formData.append("personal_email", values.email);
      formData.append("personal_phone", values.phone);
      formData.append("shared_email", values.mailPartner);
      formData.append("whatsapp_number", values.phonePartner);
      formData.append("is_active", values.isActive);

      if (values.image) {
        formData.append("image", values.image);
      }

      // Call API function
      if (mode === "edit") {
        await updateInstructorDataApi(instructorId, formData);
      } else {
        await addNewInstructorApi(formData);
      }

      // Show success alert
      Swal.fire({
        title: "Success!",
        text: "Instructor data submited successfully.",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        resetForm();
        setImage("");
        navigate("/instructors-management");
      });
    } catch (error) {
      console.error("Error submitting form:", error);

      // Show error alert
      Swal.fire({
        title: "Error!",
        text: error.response.data.message.join(" , "),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false); // Stop form submitting state
    }
  };

  const handleRemoveImage = (name, formField) => {
    setImage("");
    formField(name, "");
  };

  return (
    <>
      <p className="tw-text-[#BF0404] tw-text-end tw-mb-1">
        * means this field is required
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={StepOneSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          values,
          isSubmitting,
        }) => (
          <Form className="d-flex flex-column">
            <div className="card !tw-rounded-xl">
              <div className="card-body tw-shadow-xl tw-rounded-xl">
                <div className="row">
                  {/* nameEn */}

                  <FormField
                    name={"nameEn"}
                    label={"Name English"}
                    placeholder={"Name In English"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.nameEn}
                  />

                  {/* nameAr */}
                  <FormField
                    name={"nameAr"}
                    label={"Name Arabic"}
                    placeholder={"Name In Arabic"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.nameAr}
                  />

                  {/* titleEn */}
                  <FormField
                    name={"titleEn"}
                    label={"Title English"}
                    placeholder={"Title In English"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.titleEn}
                  />

                  {/* titleAr */}
                  <FormField
                    name={"titleAr"}
                    label={"Title Arabic"}
                    placeholder={"Title In Arabic"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.titleAr}
                  />
                  {/* Bio English */}
                  <TextareaForm
                    name={"bioEn"}
                    placeholder={"Bio In English"}
                    touched={touched.bioEn}
                    errors={errors.bioEn}
                    handleBlur={handleBlur}
                    label={"Bio English"}
                  />
                  {/* Bio Arabic */}
                  <TextareaForm
                    name={"bioAr"}
                    placeholder={"Bio In Arabic"}
                    touched={touched.bioAr}
                    errors={errors.bioAr}
                    handleBlur={handleBlur}
                    label={"Bio Arabic"}
                  />

                  {/* Email */}
                  <FormField
                    type="email"
                    name={"email"}
                    label={"email"}
                    placeholder={"Email"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.email}
                  />

                  {/* Phone */}
                  <FormField
                    type="number"
                    name={"phone"}
                    label={"phone"}
                    placeholder={"Phone Number"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    value={values.phone}
                  />

                  {/* mailPartner */}
                  <FormField
                    required={false}
                    type="email"
                    name={"mailPartner"}
                    label={"Contact Mail Shared With Partner"}
                    placeholder={"Enter a Email"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.mailPartner}
                  />

                  {/* phonePartner */}
                  <FormField
                    required={false}
                    type="number"
                    name={"phonePartner"}
                    label={"WhatsApp Number Shared With Partner"}
                    placeholder={"WhatsApp Number Shared With Partner"}
                    touched={touched}
                    errors={errors}
                    col_md={"6"}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    value={values.phonePartner}
                  />

                  {/* Image */}
                  <FileInput
                    name="image"
                    label="Upload Image"
                    value={image}
                    onChange={setFieldValue}
                    onRemove={handleRemoveImage}
                    error={errors.image}
                    touched={touched.image}
                    setPhoto={setImage}
                  />
                  {/* Is Active */}
                  <DropdownForm
                    handleChange={handleChange}
                    name={"isActive"}
                    dataToDrop={IS_ACTIVE}
                    error={errors.isActive}
                    touched={touched.isActive}
                    label={"Is Active"}
                    placeholder={"Is Active"}
                  />
                  <ConfirmCheckBox
                    label="I confirm that, the Instructor data is correct."
                    name="confirm"
                  />
                  <div className="col-md-12 tw-flex">
                    <button
                      type="submit"
                      className="btn btn-primary ml-auto !tw-w-[150px] !tw-mt-4"
                    >
                      {isSubmitting ? (
                        <RowLoader paddingStyle="tw-py-0" />
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddOrEditInstructor;
